import { Quaternion as ThreeQuaternion, Euler, Vector3 } from 'three';

import {
  slerpQuaternions,
  EulerAngleToQuaternion,
  quaternionMultiply,
} from '@sb/geometry';
import type {
  CartesianPosition,
  CartesianPose,
  Quaternion,
} from '@sb/geometry';

export type Rng = () => number;

/* eslint-disable no-bitwise */
export function makeRng(seed: number): Rng {
  let s = seed >>> 0;

  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);

    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function resolveSeed(seed: number | null): number {
  if (seed != null) return seed >>> 0;

  return (Date.now() ^ Math.floor(Math.random() * 0x100000000)) >>> 0;
}
/* eslint-enable no-bitwise */

export interface ConeRadiusParams {
  narrowRadius: number;
  wideRadius: number;
  approachLength: number;
}

export function radiusAtAxialPosition(
  t: number,
  params: ConeRadiusParams,
): number {
  if (t <= 0) return params.narrowRadius;
  const u = Math.min(1, t / params.approachLength);

  return params.narrowRadius + u * (params.wideRadius - params.narrowRadius);
}

export function levelPositions(
  levelCount: number,
  bufferLength: number,
  approachLength: number,
): number[] {
  const start = -bufferLength;
  const end = approachLength;
  if (levelCount === 1) return [start];
  const n = levelCount - 1;
  const positions: number[] = [];

  for (let i = 0; i <= n; i += 1) {
    // Numerically stable lerp: ((n-i)*start + i*end) / n avoids rounding errors
    // that arise from the equivalent start + (i/n)*(end-start) form.
    positions.push(((n - i) * start + i * end) / n);
  }

  return positions;
}

export interface PerLevelInput {
  radius: number;
  height: number;
}

export interface AxialBand {
  tStart: number;
  tEnd: number;
  radius: number;
}

/**
 * Build the axial sampling bands for per-level mode. Levels stack from the
 * target (t = 0) toward the starting pose; their heights are treated as
 * relative proportions and normalised so the cumulative bands fill
 * [0, approachLength] (the starting pose still sets the total length). When
 * bufferLength > 0 an extra band [-bufferLength, 0] is prepended at the lowest
 * level's radius, so the volume extends past the target as it does in linear
 * mode. Bands are returned in ascending-t order (buffer first, top of stack
 * last).
 */
export function buildPerLevelBands(
  levels: PerLevelInput[],
  bufferLength: number,
  approachLength: number,
): AxialBand[] {
  const bands: AxialBand[] = [];
  const lowestRadius = levels.length > 0 ? levels[0].radius : 0;

  if (bufferLength > 0) {
    bands.push({ tStart: -bufferLength, tEnd: 0, radius: lowestRadius });
  }

  const totalHeight = levels.reduce((sum, lvl) => sum + lvl.height, 0);
  if (totalHeight <= 0) return bands;

  // `cursor` accumulates heights in the same order as `totalHeight`, so the
  // final band's tEnd lands exactly on approachLength.
  let cursor = 0;

  for (const lvl of levels) {
    const tStart = (cursor / totalHeight) * approachLength;
    cursor += lvl.height;
    const tEnd = (cursor / totalHeight) * approachLength;
    bands.push({ tStart, tEnd, radius: lvl.radius });
  }

  return bands;
}

export function levelWeights(
  positions: number[],
  approachLength: number,
  targetEmphasis: number,
): number[] {
  const k = 9 * targetEmphasis;

  return positions.map((t) => 1 / (1 + (Math.abs(t) / approachLength) * k));
}

export function pointsPerLevel(
  weights: number[],
  totalSamples: number,
): number[] {
  const sum = weights.reduce((a, b) => a + b, 0);
  const counts = weights.map((w) => Math.round((w / sum) * totalSamples));
  let diff = totalSamples - counts.reduce((a, b) => a + b, 0);

  // Distribute the leftover (positive or negative) into the highest-weight
  // level, then next-highest, etc., until diff == 0.
  const order = weights
    .map((w, i) => ({ w, i }))
    .sort((a, b) => b.w - a.w)
    .map((x) => x.i);

  let cursor = 0;
  // Hard cap iterations to guarantee termination. In normal operation we
  // converge in at most `weights.length` passes; the cap guards against
  // pathological inputs (e.g. all-zero counts but negative diff).
  const maxIterations = order.length * (Math.abs(diff) + 1) + 1;
  let progressInPass = false;

  for (let step = 0; diff !== 0 && step < maxIterations; step += 1) {
    const idx = order[cursor % order.length];

    if (diff > 0) {
      counts[idx] += 1;
      diff -= 1;
      progressInPass = true;
    } else if (counts[idx] > 0) {
      counts[idx] -= 1;
      diff += 1;
      progressInPass = true;
    }

    cursor += 1;

    // After each full pass over `order`, check we made progress; if not, bail
    // and absorb the residue in the highest-weight nonzero count.
    if (cursor % order.length === 0) {
      if (!progressInPass) break;
      progressInPass = false;
    }
  }

  if (diff > 0) {
    // Dump positive residue on the highest-weight bucket.
    counts[order[0]] += diff;
  } else if (diff < 0) {
    // Walk highest-weight buckets, draining each fully before moving on,
    // so the returned counts always sum to totalSamples.
    for (const i of order) {
      if (diff === 0) break;
      const take = Math.min(counts[i], -diff);
      counts[i] -= take;
      diff += take;
    }
  }

  return counts;
}

export interface DiskSample {
  rRadial: number;
  theta: number;
}

export function sampleDiskPoint(rng: Rng, radius: number): DiskSample {
  const u = rng();
  const v = rng();

  return {
    rRadial: radius * Math.sqrt(u),
    theta: 2 * Math.PI * v,
  };
}

export interface AxisFrame {
  axis: CartesianPosition;
  e1: CartesianPosition;
  e2: CartesianPosition;
}

export function buildAxisFrame(
  target: CartesianPosition,
  starting: CartesianPosition,
): AxisFrame {
  const axis = new Vector3(
    starting.x - target.x,
    starting.y - target.y,
    starting.z - target.z,
  );

  const length = axis.length();

  if (length === 0) {
    throw new Error(
      'SamplePositioningApproach: starting position equals target position; cannot derive axis',
    );
  }

  axis.divideScalar(length);

  // Pick a reference vector that is not parallel to axis, then build basis.
  const ref =
    Math.abs(axis.x) < 0.9 ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0);

  const e1 = new Vector3().crossVectors(axis, ref).normalize();
  const e2 = new Vector3().crossVectors(axis, e1).normalize();

  return {
    axis: { x: axis.x, y: axis.y, z: axis.z },
    e1: { x: e1.x, y: e1.y, z: e1.z },
    e2: { x: e2.x, y: e2.y, z: e2.z },
  };
}

export function axialFramePoint(
  target: CartesianPosition,
  frame: AxisFrame,
  t: number,
  rRadial: number,
  theta: number,
): CartesianPosition {
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);

  return {
    x:
      target.x +
      t * frame.axis.x +
      rRadial * (cos * frame.e1.x + sin * frame.e2.x),
    y:
      target.y +
      t * frame.axis.y +
      rRadial * (cos * frame.e1.y + sin * frame.e2.y),
    z:
      target.z +
      t * frame.axis.z +
      rRadial * (cos * frame.e1.z + sin * frame.e2.z),
  };
}

export interface JitterRanges {
  rollRange: number;
  pitchRange: number;
  yawRange: number;
}

// Slerps orientation along the approach axis: qTarget at t = 0 (cone base, at
// the target) → qStarting at t = approachLength (cone top, at the starting
// pose). t ≤ 0 is the buffer region beyond the target, where we hold qTarget.
export function orientationAt(
  t: number,
  qStarting: Quaternion,
  qTarget: Quaternion,
  approachLength: number,
): Quaternion {
  if (t <= 0) return { ...qTarget };
  const alpha = Math.min(1, t / approachLength);

  return slerpQuaternions(qTarget, qStarting, alpha);
}

export function applyJitter(
  qBase: Quaternion,
  qTarget: Quaternion,
  rng: Rng,
  jitter: JitterRanges,
): Quaternion {
  const sample = (range: number) => (rng() * 2 - 1) * range;

  // UI convention: tool axis is tooltip +Z, so Yaw is the twist around the
  // tool axis (Rz). Internally, target poses use local +X as the tool axis
  // (SB target-pose convention; see targetToROS2Tooltip), with the rotation
  // mapping target +X ↔ tooltip +Z, target +Y ↔ tooltip +X, target +Z ↔
  // tooltip +Y. So in target-local XYZ Euler: yaw → euler.x (tool axis),
  // roll → euler.y, pitch → euler.z.
  const qJitter = EulerAngleToQuaternion({
    x: sample(jitter.yawRange),
    y: sample(jitter.rollRange),
    z: sample(jitter.pitchRange),
  });

  // Apply jitter in the target tooltip frame (matches applyAxisLocks), so the
  // configured roll/pitch/yaw ranges always refer to rotations in the target's
  // tooltip frame — regardless of qBase. Equivalent to
  // qTarget · qJitter · qTarget^-1 · qBase.
  const qTargetInv: Quaternion = {
    w: qTarget.w,
    i: -qTarget.i,
    j: -qTarget.j,
    k: -qTarget.k,
  };

  return quaternionMultiply(
    qTarget,
    quaternionMultiply(qJitter, quaternionMultiply(qTargetInv, qBase)),
  );
}

export interface AxisLocks {
  roll: boolean;
  pitch: boolean;
  yaw: boolean;
}

export function applyAxisLocks(
  q: Quaternion,
  qTarget: Quaternion,
  locks: AxisLocks,
): Quaternion {
  if (!locks.roll && !locks.pitch && !locks.yaw) return { ...q };

  // q_local = q_target^-1 · q   — delta in target-local frame
  const qTargetThree = new ThreeQuaternion(
    qTarget.i,
    qTarget.j,
    qTarget.k,
    qTarget.w,
  );

  const qThree = new ThreeQuaternion(q.i, q.j, q.k, q.w);
  const qLocal = qTargetThree.clone().invert().multiply(qThree);

  // Decompose into XYZ Euler in target-local frame. Mapping mirrors
  // applyJitter: yaw → euler.x (tool axis = target +X), roll → euler.y,
  // pitch → euler.z.
  const euler = new Euler().setFromQuaternion(qLocal, 'XYZ');
  if (locks.yaw) euler.x = 0;
  if (locks.roll) euler.y = 0;
  if (locks.pitch) euler.z = 0;

  // Recompose and apply on top of target: q_final = q_target · q_local'
  const qLocalPrime = new ThreeQuaternion().setFromEuler(euler);
  const qFinal = qTargetThree.clone().multiply(qLocalPrime);

  return { w: qFinal.w, i: qFinal.x, j: qFinal.y, k: qFinal.z };
}

export interface GenerateWaypointsInput {
  targetPose: CartesianPose;
  startingPose: CartesianPose;
  approachLength: number;
  bufferLength: number;
  narrowRadius: number;
  wideRadius: number;
  sampleCount: number;
  levelCount: number;
  targetEmphasis: number;
  orientationJitter: JitterRanges;
  lockedAxes: AxisLocks;
  seedUsed: number;
  // Optional → callers/tests that omit it get the original linear behavior.
  radiusMode?: 'linear' | 'perLevel';
  levels?: PerLevelInput[];
  // Defaults to 'discrete' (the original level/band behavior). 'continuous'
  // draws each point's height from a Gaussian centred on the target and uses
  // levelCount only as the execution-batch count.
  heightMode?: 'discrete' | 'continuous';
  // Continuous mode only: Gaussian height std-dev as a fraction of
  // approachLength (sigma = heightSpread * approachLength). Defaults to 0.5.
  heightSpread?: number;
}

/**
 * Generate sweep waypoints grouped by level. Levels are returned in ascending
 * axial-t order (deepest buffer first, top-of-cone last). The final
 * exact-target waypoint is appended to the last level — `flat()`-ing the
 * result matches the layout `generateWaypoints` returns.
 */
export function generateWaypointsByLevel(
  input: GenerateWaypointsInput,
): CartesianPose[][] {
  const target = {
    x: input.targetPose.x,
    y: input.targetPose.y,
    z: input.targetPose.z,
  };

  const starting = {
    x: input.startingPose.x,
    y: input.startingPose.y,
    z: input.startingPose.z,
  };

  const qTarget: Quaternion = {
    w: input.targetPose.w,
    i: input.targetPose.i,
    j: input.targetPose.j,
    k: input.targetPose.k,
  };

  const qStarting: Quaternion = {
    w: input.startingPose.w,
    i: input.startingPose.i,
    j: input.startingPose.j,
    k: input.startingPose.k,
  };

  const frame = buildAxisFrame(target, starting);

  if (input.heightMode === 'continuous') {
    return generateContinuousWaypointsByLevel(
      input,
      target,
      frame,
      qTarget,
      qStarting,
    );
  }

  if (
    input.radiusMode === 'perLevel' &&
    input.levels != null &&
    input.levels.length > 0
  ) {
    return generatePerLevelWaypointsByLevel(
      input,
      target,
      frame,
      qTarget,
      qStarting,
    );
  }

  const positions = levelPositions(
    input.levelCount,
    input.bufferLength,
    input.approachLength,
  );

  const weights = levelWeights(
    positions,
    input.approachLength,
    input.targetEmphasis,
  );

  const counts = pointsPerLevel(weights, input.sampleCount);

  const rng = makeRng(input.seedUsed);
  // Sweep: deepest buffer (t = -bufferLength) -> top of cone (t = approachLength).
  // levelPositions already returns ascending t, so use it directly.
  const levels: CartesianPose[][] = [];

  for (let idx = 0; idx < positions.length; idx += 1) {
    const t = positions[idx];
    const r = radiusAtAxialPosition(t, input);
    const n = counts[idx];
    const level: CartesianPose[] = [];
    const qBase = orientationAt(t, qStarting, qTarget, input.approachLength);

    for (let i = 0; i < n; i += 1) {
      const { rRadial, theta } = sampleDiskPoint(rng, r);
      const p = axialFramePoint(target, frame, t, rRadial, theta);

      const qJittered = applyJitter(
        qBase,
        qTarget,
        rng,
        input.orientationJitter,
      );

      const qFinalWp = applyAxisLocks(qJittered, qTarget, input.lockedAxes);

      level.push({
        x: p.x,
        y: p.y,
        z: p.z,
        w: qFinalWp.w,
        i: qFinalWp.i,
        j: qFinalWp.j,
        k: qFinalWp.k,
      });
    }

    levels.push(level);
  }

  // Final exact-target waypoint with no jitter, appended to the last level so
  // generateWaypoints (flattened view) lands on the exact target last.
  levels[levels.length - 1].push({ ...input.targetPose });

  return levels;
}

/**
 * Per-level variant of {@link generateWaypointsByLevel}. The volume is a stack
 * of cylinders (one per configured level) plus an optional buffer band; each
 * sample is placed volumetrically — a random axial position within its band
 * combined with a uniform disk point at that band's radius. Returns the same
 * ascending-t `CartesianPose[][]` layout as the linear path (buffer first, top
 * of stack last, exact target appended to the final band).
 */
function generatePerLevelWaypointsByLevel(
  input: GenerateWaypointsInput,
  target: CartesianPosition,
  frame: AxisFrame,
  qTarget: Quaternion,
  qStarting: Quaternion,
): CartesianPose[][] {
  const bands = buildPerLevelBands(
    input.levels ?? [],
    input.bufferLength,
    input.approachLength,
  );

  // Weight bands by their centre so targetEmphasis biases samples toward the
  // target exactly as in linear mode, then split the sample budget by weight.
  const centers = bands.map((b) => (b.tStart + b.tEnd) / 2);

  const weights = levelWeights(
    centers,
    input.approachLength,
    input.targetEmphasis,
  );

  const counts = pointsPerLevel(weights, input.sampleCount);

  const rng = makeRng(input.seedUsed);
  const levels: CartesianPose[][] = [];

  for (let idx = 0; idx < bands.length; idx += 1) {
    const band = bands[idx];
    const n = counts[idx];
    const level: CartesianPose[] = [];

    for (let i = 0; i < n; i += 1) {
      // Volumetric placement: a random axial position within the band fills the
      // cylinder height, then a uniform-area disk point at the band radius.
      const t = band.tStart + rng() * (band.tEnd - band.tStart);
      const { rRadial, theta } = sampleDiskPoint(rng, band.radius);
      const p = axialFramePoint(target, frame, t, rRadial, theta);
      const qBase = orientationAt(t, qStarting, qTarget, input.approachLength);

      const qJittered = applyJitter(
        qBase,
        qTarget,
        rng,
        input.orientationJitter,
      );

      const qFinalWp = applyAxisLocks(qJittered, qTarget, input.lockedAxes);

      level.push({
        x: p.x,
        y: p.y,
        z: p.z,
        w: qFinalWp.w,
        i: qFinalWp.i,
        j: qFinalWp.j,
        k: qFinalWp.k,
      });
    }

    levels.push(level);
  }

  // Final exact-target waypoint, appended to the top-of-stack band so the
  // flattened sweep ends on the exact target (matches the linear path).
  levels[levels.length - 1].push({ ...input.targetPose });

  return levels;
}

/**
 * One draw from the standard normal N(0, 1) via the Box–Muller transform,
 * driven by the seeded uniform `rng`. Guards against log(0).
 */
export function sampleStandardNormal(rng: Rng): number {
  let u1 = rng();
  if (u1 <= 0) u1 = Number.MIN_VALUE;
  const u2 = rng();

  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

/**
 * Draw an axial height from a Gaussian centred on the target (t = 0) with
 * standard deviation `sigma`, restricted to [lo, hi] by rejection. Density
 * peaks at the target and decays with distance — independent of the radius at
 * each height. Falls back to a uniform draw if rejection cannot land in range
 * within a bounded number of tries (e.g. sigma huge relative to the range), so
 * the loop always terminates.
 */
function sampleGaussianHeight(
  rng: Rng,
  sigma: number,
  lo: number,
  hi: number,
): number {
  const MAX_TRIES = 100;

  for (let i = 0; i < MAX_TRIES; i += 1) {
    const t = sigma * sampleStandardNormal(rng);
    if (t >= lo && t <= hi) return t;
  }

  return lo + rng() * (hi - lo);
}

/**
 * Radius of the band containing axial position t, for per-level continuous
 * sampling. Bands are contiguous and ascending; t below the first band clamps
 * to the first radius, t at/above the last clamps to the last radius.
 */
function radiusAtBand(bands: AxialBand[], t: number): number {
  if (bands.length === 0) return 0;
  if (t <= bands[0].tStart) return bands[0].radius;

  for (const band of bands) {
    if (t <= band.tEnd) return band.radius;
  }

  return bands[bands.length - 1].radius;
}

/**
 * Continuous-height variant of {@link generateWaypointsByLevel}. Each of
 * sampleCount points draws its axial height from a Gaussian centred on the
 * target (see {@link sampleGaussianHeight}) — so samples are densest at the
 * target and thin out toward the starting pose — then a uniform-area disk point
 * within the cone / per-level radius at that height. The radius only bounds the
 * point to the configured volume; it does not affect how densely heights are
 * sampled. Points are emitted in random order (no axial sort); they are split
 * into levelCount batches purely to cap motion-plan size, and the exact target
 * is appended last so the flattened sweep still ends on the target.
 */
function generateContinuousWaypointsByLevel(
  input: GenerateWaypointsInput,
  target: CartesianPosition,
  frame: AxisFrame,
  qTarget: Quaternion,
  qStarting: Quaternion,
): CartesianPose[][] {
  const tMin = -input.bufferLength;
  const tMax = input.approachLength;

  // Gaussian std-dev as a fraction of the approach length. Guarded positive so
  // the distribution is always valid even if a caller passes 0.
  const heightSpread = input.heightSpread ?? 0.5;
  const sigma = Math.max(heightSpread, 1e-6) * input.approachLength;

  const isPerLevel =
    input.radiusMode === 'perLevel' &&
    input.levels != null &&
    input.levels.length > 0;

  const bands = isPerLevel
    ? buildPerLevelBands(
        input.levels ?? [],
        input.bufferLength,
        input.approachLength,
      )
    : [];

  const radiusAt = isPerLevel
    ? (t: number) => radiusAtBand(bands, t)
    : (t: number) => radiusAtAxialPosition(t, input);

  const rng = makeRng(input.seedUsed);

  const samples: CartesianPose[] = [];

  for (let i = 0; i < input.sampleCount; i += 1) {
    const t = sampleGaussianHeight(rng, sigma, tMin, tMax);
    const r = radiusAt(t);
    const { rRadial, theta } = sampleDiskPoint(rng, r);
    const p = axialFramePoint(target, frame, t, rRadial, theta);
    const qBase = orientationAt(t, qStarting, qTarget, input.approachLength);
    const qJittered = applyJitter(qBase, qTarget, rng, input.orientationJitter);
    const qFinalWp = applyAxisLocks(qJittered, qTarget, input.lockedAxes);

    samples.push({
      x: p.x,
      y: p.y,
      z: p.z,
      w: qFinalWp.w,
      i: qFinalWp.i,
      j: qFinalWp.j,
      k: qFinalWp.k,
    });
  }

  // Split the (unordered) samples into levelCount near-equal batches purely to
  // keep each motion plan small for the planner. The first
  // (sampleCount % batchCount) batches take one extra to absorb the remainder.
  const batchCount = Math.max(1, input.levelCount);
  const base = Math.floor(samples.length / batchCount);
  const extra = samples.length % batchCount;
  const levels: CartesianPose[][] = [];

  let cursor = 0;

  for (let b = 0; b < batchCount; b += 1) {
    const size = base + (b < extra ? 1 : 0);
    levels.push(samples.slice(cursor, cursor + size));
    cursor += size;
  }

  // Final exact-target waypoint (no jitter), appended to the last batch so the
  // flattened sweep ends precisely on the target — matches the discrete paths.
  levels[levels.length - 1].push({ ...input.targetPose });

  return levels;
}

export function generateWaypoints(
  input: GenerateWaypointsInput,
): CartesianPose[] {
  return generateWaypointsByLevel(input).flat();
}
